import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Paper,
  Divider,
  Link as MuiLink,
  Rating,
} from '@mui/material';
import { motion } from 'framer-motion';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import cleaningImage from '../womancleaning.jpg';
import cleanRoomImage from '../cleanroom.jpg';
import { useNavigate } from 'react-router-dom';
import HolidayDialog from '../components/HolidayDialog';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PhoneIcon from '@mui/icons-material/Phone';
import FacebookIcon from '@mui/icons-material/Facebook';
import GoogleIcon from '@mui/icons-material/Google';
import ScrollReveal from '../components/ScrollReveal';

import ServiceAreaMap from '../components/ServiceAreaMap';
import ImageSkeleton from '../components/ImageSkeleton';
import { Helmet } from 'react-helmet-async';

const BRAND_BLUE = '#14a9e8';
// eslint-disable-next-line no-unused-vars
const BRAND_INK = '#0b1620';

const FACEBOOK_URL = 'https://www.facebook.com/profile.php?id=61552687223734';

const GOOGLE_REVIEWS_URL =
  process.env.REACT_APP_GOOGLE_REVIEWS_URL ||
  'https://www.google.com/search?q=Lowen+Behold+Cleaning+reviews';

const GOOGLE_RATING = Number(process.env.REACT_APP_GOOGLE_RATING);
const GOOGLE_REVIEW_COUNT = Number(process.env.REACT_APP_GOOGLE_REVIEW_COUNT);
const HAS_GOOGLE_RATING =
  Number.isFinite(GOOGLE_RATING) && Number.isFinite(GOOGLE_REVIEW_COUNT);

const heroVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.15,
      duration: 0.6,
      ease: 'easeOut',
    },
  }),
};

const Home = () => {
  const [open, setOpen] = useState(false);
  const [belowFoldReady, setBelowFoldReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setOpen(true), 2500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const id = requestAnimationFrame(() => setBelowFoldReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const handleClose = () => setOpen(false);

  const serviceItems = [
    {
      title: 'Residential cleaning',
      body:
        'Recurring, deep, move-in/move-out, and seasonal refresh cleans. Great for keeping your home calm, healthy, and consistently "company-ready."',
      bullets: [
        'Standard upkeep: kitchens, bathrooms, living areas, bedrooms',
        'Deep clean upgrades: baseboards, blinds, interior windows, detail work',
        'Pet hair & high-traffic focus available (added charge may apply)',
      ],
      icon: <HomeIcon sx={{ fontSize: 26, color: BRAND_BLUE }} />,
      cta: 'Explore residential services',
      anchor: '#residential',
    },
    {
      title: 'Commercial cleaning',
      body:
        'Reliable, professional cleaning for offices and small businesses — built around your hours, traffic, and security requirements.',
      bullets: [
        'Restrooms, breakrooms, floors, trash, and common areas',
        'After-hours/early morning options to avoid disruption (added charge may apply)',
        'Site checklists and ongoing quality check-ins available',
      ],
      icon: <BusinessIcon sx={{ fontSize: 26, color: BRAND_BLUE }} />,
      cta: 'Explore commercial services',
      anchor: '#commercial',
    },
    {
      title: 'Add-ons & custom plans',
      body:
        'Dial in the perfect clean with upgrades and a plan that matches your priorities — one-time or recurring.',
      bullets: [
        'Inside fridge/oven, interior windows, baseboards, blinds, ceiling fans',
        'High-touch disinfecting, conference room reset, short-term rental turnovers',
        'Priority rooms focus, rotating checklist, seasonal refresh packages',
      ],
      icon: <AddCircleOutlineIcon sx={{ fontSize: 26, color: BRAND_BLUE }} />,
      cta: 'See add-ons & options',
      anchor: '#add-ons',
    },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      <Helmet>
        <title>Lowen Behold Cleaning | Professional Cleaning in Eugene &amp; Springfield, OR</title>
        <meta name="description" content="Family-owned house cleaning and commercial cleaning in Eugene & Springfield, OR. Deep cleans, move-in/out, Airbnb turnovers, and recurring service. Licensed & insured — free estimates." />
        <link rel="canonical" href="https://www.lowenbeholdcleaning.com/" />
      </Helmet>

      <HolidayDialog open={open} onClose={handleClose} />

      {/* HERO */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 0,
          borderBottom: '1px solid rgba(0,0,0,.08)',
          bgcolor: 'background.default',
        }}
      >
        <Container sx={{ py: { xs: 3, md: 4 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                custom={0}
                initial="hidden"
                animate="visible"
                variants={heroVariants}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: 34, md: 40 },
                    fontWeight: 800,
                    lineHeight: 1.1,
                    color: 'text.primary',
                  }}
                >
                  We Clean Exceptionally...So You Don't Have To!
                </Typography>
              </motion.div>

              <motion.div
                custom={1}
                initial="hidden"
                animate="visible"
                variants={heroVariants}
              >
                <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
                  Reliable, detail-focused cleaning in Eugene & Springfield.
                  We show up on time, work carefully, and make it right if anything's missed.
                </Typography>
              </motion.div>

              <motion.div
                custom={2}
                initial="hidden"
                animate="visible"
                variants={heroVariants}
              >
                <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    onClick={() => navigate('/quote')}
                    variant="contained"
                    size="large"
                    sx={{
                      bgcolor: BRAND_BLUE,
                      textTransform: 'none',
                      '&:hover': { bgcolor: '#1298cf' },
                    }}
                  >
                    Get a Free Quote
                  </Button>
                  <Button
                    onClick={() => navigate('/services')}
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: `${BRAND_BLUE}55`,
                      color: 'text.primary',
                      textTransform: 'none',
                      '&:hover': { bgcolor: 'rgba(20,169,232,.08)' },
                    }}
                  >
                    See Services
                  </Button>
                  <Button
                    component="a"
                    href="tel:+15416061706"
                    variant="outlined"
                    size="large"
                    startIcon={<PhoneIcon />}
                    sx={{
                      borderColor: `${BRAND_BLUE}55`,
                      color: 'text.primary',
                      textTransform: 'none',
                      '&:hover': { bgcolor: 'rgba(20,169,232,.08)' },
                    }}
                  >
                    (541) 606-1706
                  </Button>
                </Box>
              </motion.div>

              <motion.div
                custom={3}
                initial="hidden"
                animate="visible"
                variants={heroVariants}
              >
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  Supplies included &bull; Flexible scheduling &bull; Custom checklists available
                </Typography>

                <Box sx={{ mt: 2.25 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1.25,
                      alignItems: 'center',
                    }}
                  >
                 {/*   <MuiLink
                      href={FACEBOOK_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: 'text.primary',
                        fontWeight: 700,
                        '&:hover': { textDecoration: 'underline' },
                        '&:focus-visible': {
                          outline: `2px solid ${BRAND_BLUE}`,
                          outlineOffset: '2px',
                          borderRadius: '6px',
                        },
                      }}
                      aria-label="Open our Facebook page in a new tab"
                    >
                      <FacebookIcon sx={{ fontSize: 18, color: BRAND_BLUE }} />
                      Facebook
                    </MuiLink> */}

                    <MuiLink
                      href={GOOGLE_REVIEWS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: 'text.primary',
                        fontWeight: 700,
                        '&:hover': { textDecoration: 'underline' },
                        '&:focus-visible': {
                          outline: `2px solid ${BRAND_BLUE}`,
                          outlineOffset: '2px',
                          borderRadius: '6px',
                        },
                      }}
                      aria-label="Open our Google reviews in a new tab"
                    >
                      <GoogleIcon sx={{ fontSize: 18, color: BRAND_BLUE }} />
                      Google Reviews
                    </MuiLink>

                    {HAS_GOOGLE_RATING && (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1,
                          py: 0.5,
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          border: '1px solid rgba(0,0,0,.08)',
                                                }}
                      aria-label={`Google rating ${GOOGLE_RATING} out of 5`}
                    >
                     {/*}     }} //removed review count ^^
                      aria-label={`Google rating ${GOOGLE_RATING} out of 5 based on ${GOOGLE_REVIEW_COUNT} reviews`}
                    > */}
                        <Rating
                          value={GOOGLE_RATING}
                          precision={0.1}
                          readOnly
                          size="small"
                        />
                        <Typography sx={{ fontSize: '0.9rem', color: 'text.primary' }}>
                          <Box component="span" sx={{ fontWeight: 800 }}>
                            {GOOGLE_RATING.toFixed(1)}
                          </Box>{' '}
                         {/* //removed review count 
                          <Box component="span" sx={{ color: 'text.secondary' }}>
                            ({GOOGLE_REVIEW_COUNT} reviews)
                          </Box>
                          */}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.3, ease: 'easeOut' }}
              >
                <Box
                  sx={{
                    border: '1px solid rgba(0,0,0,.08)',
                    bgcolor: '#f6fbff',
                    borderRadius: 3,
                    height: { xs: 260, md: 360 },
                    display: 'grid',
                    placeItems: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <ImageSkeleton
                    src={cleaningImage}
                    alt="Professional cleaner wiping down a kitchen counter"
                    width="100%"
                    height="100%"
                    style={{ objectFit: 'cover' }}
                  />
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Paper>

      {belowFoldReady && (<>
      {/* INSURED/BONDED STRIP */}
      <ScrollReveal>
        <Container sx={{ py: { xs: 2.25, md: 2.75 } }}>
            <Box
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                bgcolor: 'rgba(20,169,232,.04)',
                border: '1px solid rgba(0,0,0,.04)',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                <HealthAndSafetyIcon sx={{ color: BRAND_BLUE, mt: '2px' }} />
                <Box>
                  <Typography sx={{ fontWeight: 900, color: 'text.primary', lineHeight: 1.2, fontSize: { xs: 18, md: 20 } }}>
                    Fully insured &amp; bonded
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                    Peace of mind for every visit — your home and workplace are treated with care and professionalism.
                  </Typography>
                </Box>
              </Box>

              <Button
                onClick={() => navigate('/quote')}
                variant="contained"
                sx={{
                  bgcolor: BRAND_BLUE,
                  textTransform: 'none',
                  fontWeight: 800,
                  px: 2.5,
                  '&:hover': { bgcolor: '#1298cf' },
                  alignSelf: { xs: 'stretch', md: 'auto' },
                }}
                endIcon={<ArrowForwardIcon />}
              >
                Get a Free Quote
              </Button>
            </Box>
          </Container>
      </ScrollReveal>

      {/* SERVICE TYPES STRIP */}
      <Container sx={{ py: { xs: 4, md: 5 } }}>
        <Grid container spacing={3}>
          {serviceItems.map((item, idx) => (
            <Grid item xs={12} md={4} key={item.title}>
              <ScrollReveal delay={idx * 0.1}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(0,0,0,.10)',
                    p: 2.75,
                    height: '100%',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {item.icon}
                    <Typography variant="h6" sx={{ fontWeight: 900, color: 'text.primary', lineHeight: 1.2 }}>
                      {item.title}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                    {item.body}
                  </Typography>

                  <Divider sx={{ my: 1.25, borderColor: 'rgba(0,0,0,.08)' }} />

                  <Box component="ul" sx={{ m: 0, pl: '1.2rem' }}>
                    {item.bullets.map((b, i) => (
                      <Box component="li" key={i} sx={{ mb: 0.75 }}>
                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                          {b}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Button
                      onClick={() => navigate(`/services${item.anchor}`)}
                      size="small"
                      sx={{
                        color: BRAND_BLUE,
                        textTransform: 'none',
                        px: 0,
                        fontWeight: 800,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                      endIcon={<ArrowForwardIcon />}
                    >
                      {item.cta}
                    </Button>

                    {item.title === 'Add-ons & custom plans' && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}>
                        Add-ons are available for an added charge. We'll price everything clearly in your quote.
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </ScrollReveal>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* BRAND PROMISE */}
      <ScrollReveal>
        <Container sx={{ pb: 4 }}>
          <Typography variant="h5" align="center" sx={{ fontWeight: 800, color: 'text.primary' }}>
            Spotless results. Professional finish. Honest service.
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{ mt: 1.5, maxWidth: 640, mx: 'auto', color: 'text.secondary' }}
          >
            From one-time deep cleans to ongoing maintenance, we tailor each visit to
            your home or office so you get exactly the clean you need — nothing more,
            nothing less.
          </Typography>
        </Container>
      </ScrollReveal>

      {/* BENEFITS + SECONDARY IMAGE */}
      <ScrollReveal>
        <Container sx={{ pb: { xs: 6, md: 8 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: '1px solid rgba(0,0,0,.08)',
                  boxShadow: '0 4px 8px rgba(0,0,0,.06)',
                }}
              >
                <ImageSkeleton
                  src={cleanRoomImage}
                  alt="Bright, freshly cleaned living room with organized surfaces"
                  width="100%"
                  height={300}
                  borderRadius={3}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', mb: 1 }}>
                Benefits of Our Cleaning Services
              </Typography>

              <List sx={{ pt: 0 }}>
                {[
                  'Save time: Focus on work, family, and life — we handle the cleaning.',
                  'Professional results: Detail-oriented cleaners, consistent checklists.',
                  'Stress relief: Come home or walk into work to a space that actually feels clean.',
                  'Supplies included: We bring our own products and equipment.',
                  'Flexible scheduling: One-time, weekly, bi-weekly, or monthly visits.',
                  "Custom plans: Tell us what matters most — we'll build around your priorities.",
                ].map((benefit, index) => (
                  <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <FiberManualRecordIcon fontSize="small" sx={{ color: BRAND_BLUE }} />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ color: 'text.primary' }} primary={benefit} />
                  </ListItem>
                ))}
              </List>

              <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                  onClick={() => navigate('/quote')}
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: BRAND_BLUE,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#1298cf' },
                  }}
                >
                  Contact us for a free estimate
                </Button>
                <Button
                  onClick={() => navigate('/about')}
                  variant="text"
                  size="large"
                  sx={{ color: 'text.primary', textTransform: 'none' }}
                >
                  Learn more about us
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </ScrollReveal>

      {/* HOW IT WORKS */}
      <ScrollReveal>
        <Container sx={{ pb: { xs: 6, md: 8 } }}>
          <Box
            sx={{
              borderRadius: 3,
              bgcolor: 'rgba(20,169,232,.04)',
              border: '1px solid rgba(0,0,0,.04)',
              p: { xs: 3, md: 4 },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', mb: 2 }}>
              How it works
            </Typography>

            <Grid container spacing={3}>
              {[
                {
                  title: 'Request your free quote',
                  body: "Tell us about your home or office, your priorities, and how often you'd like cleaning.",
                },
                {
                  title: 'We build a custom plan',
                  body: 'We recommend a cleaning plan and schedule that fits your space, budget, and comfort level.',
                },
                {
                  title: 'Relax in a freshly cleaned space',
                  body: 'Our team arrives on time, brings supplies, and cleans with care — so you can focus on everything else.',
                },
              ].map((step, idx) => (
                <Grid key={idx} item xs={12} md={4}>
                  <ScrollReveal delay={idx * 0.12}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          border: `2px solid ${BRAND_BLUE}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: 16,
                          color: BRAND_BLUE,
                          flexShrink: 0,
                        }}
                      >
                        {idx + 1}
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          {step.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {step.body}
                        </Typography>
                      </Box>
                    </Box>
                  </ScrollReveal>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Button
                onClick={() => navigate('/quote')}
                variant="contained"
                size="large"
                sx={{
                  bgcolor: BRAND_BLUE,
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#1298cf' },
                }}
              >
                Get a Free Quote
              </Button>
            </Box>
          </Box>
        </Container>
      </ScrollReveal>

      {/* SERVICE AREA MAP */}
      <ScrollReveal>
        <Container sx={{ pb: { xs: 6, md: 8 } }}>
          <ServiceAreaMap />
        </Container>
      </ScrollReveal>

      {/* SERVICE AREA TEXT STRIP */}
      <Box
        sx={{
          borderTop: '1px solid rgba(0,0,0,.06)',
          bgcolor: 'background.paper',
          py: 3,
          mt: 2,
        }}
      >
        <Container>
          <Typography variant="body2" align="center" sx={{ color: 'text.secondary' }}>
            Proudly serving Eugene, Springfield, and nearby surrounding areas.
          </Typography>
        </Container>
      </Box>
      </>)}
    </Box>
  );
};

export default Home;
